import { IntegrationCard } from './IntegrationCard';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useIntegration, useConnectJira, useDisconnectJira } from '../../hooks/useIntegrations';
import { useIntegrationAvailability } from '../../hooks/useIntegrationAvailability';
import { log } from '@/lib/logger';
import { JIRA_SYNC_RECONNECT_COPY } from '@overlayqa/shared';

log.info('[JiraIntegrationCard] Module loading...');

export const JIRA_CARD_DESCRIPTION = 'Push captured issues to Jira with screenshots and full technical context.';

export function JiraIntegrationCard() {
  const { integration, isConnected, isLoading, refetch } = useIntegration('jira');
  const { available, reason: unavailableReason } = useIntegrationAvailability('jira');
  const needsSyncReconnect = isConnected === true && integration?.syncReconnectRequired === true;
  const connectMutation = useConnectJira();
  const disconnectMutation = useDisconnectJira();

  const [actionError, setActionError] = useState<string | null>(null);

  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    // Never fire a connect the backend can't complete — it's a guaranteed 500.
    if (!available) return;
    setActionError(null);
    connectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to connect Jira. Try again.');
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to disconnect Jira. Try again.');
      },
    });
  };

  const metadata = integration?.providerMetadata;
  return (
    <IntegrationCard
      provider="jira"
      loading={isLoading}
      tile={<IntegrationTile color="var(--color-jira)"><JiraIcon /></IntegrationTile>}
      name="Jira"
      description={JIRA_CARD_DESCRIPTION}
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        available,
        unavailableReason,
        reconnectMessage: needsSyncReconnect ? JIRA_SYNC_RECONNECT_COPY : undefined,
        connectedAt: integration?.createdAt,
        rows: [['Site', [metadata?.siteName, metadata?.siteUrl?.replace(/^https?:\/\//, '')].filter(Boolean).join(', ')]],
        twoWaySync: true,
      }}
      errors={[actionError]}
    />
  );
}

export function JiraIcon() {
  return (
    <svg width="12" height="12" viewBox="0 -30.632 255.324 285.956" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M244.658 0H121.707a55.502 55.502 0 0 0 55.502 55.502h22.649V77.37c.02 30.625 24.841 55.447 55.466 55.467V10.666C255.324 4.777 250.55 0 244.658 0z" fill="white"/>
      <path d="M183.822 61.262H60.872c.019 30.625 24.84 55.447 55.466 55.467h22.649v21.938c.039 30.625 24.877 55.43 55.502 55.43V71.93c0-5.891-4.776-10.667-10.667-10.667z" fill="white" opacity="0.75"/>
      <path d="M122.951 122.489H0c0 30.653 24.85 55.502 55.502 55.502h22.72v21.867c.02 30.597 24.798 55.408 55.396 55.466V133.156c0-5.891-4.776-10.667-10.667-10.667z" fill="white" opacity="0.5"/>
    </svg>
  );
}

log.info('[JiraIntegrationCard] Module loaded');
